import { usePricingReviewWrite } from './usePricingReviewWrite';
import { lazy, Suspense, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can, formatQty } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { PageRoot, PageHeader, KpiGrid, KpiCard } from '../ui/Page';
import { UnderlineTabStrip, UnderlineTabs } from '../ui/Tabs';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import {
  ErrorState,
  ForbiddenState,
  FiguresNotice,
  OfflineState,
  shownState,
  StaleNotice,
  refreshFailed,
} from '../ui/QueryStates';
import { PageSkeleton } from '../ui/Skeleton';
import { money } from '../money';
import { StockTab } from './StockTab';
// Load the pricing view on demand; its retained write owner remains above all tabs.
const PricesTab = lazy(() =>
  import('./PricesTab').then((module) => ({ default: module.PricesTab })),
);
import { ReorderTab } from './ReorderTab';
import { PurchaseOrdersTab } from './PurchaseOrdersTab';
const VendorsTab = lazy(() =>
  import('./VendorsTab').then((module) => ({ default: module.VendorsTab })),
);
import { VanTemplatesTab } from './VanTemplatesTab';
import { useSession } from '../session';
import type { OnHandItem, StockLocation } from './types';
import { IconInventory } from '../icons';

const TABS = [
  { key: 'stock', label: 'Stock' },
  { key: 'reorder', label: 'Reorder' },
  { key: 'orders', label: 'Purchase orders' },
  { key: 'vendors', label: 'Vendors' },
];

/**
 * Inventory (PRD v2 §6) — the thing Jobber has no answer for. Four tabs in the order the
 * work actually happens: what's on the shelf, what's below where you want it, what you've
 * ordered, and who you order from.
 */
export function InventoryPage() {
  const trpc = useTRPC();
  const session = useSession();
  const manager = can(session.role, 'inventory', 'manage');
  const pricingAllowed = session.role === 'owner' || session.role === 'office';
  const qc = useQueryClient();
  const onHand = useQuery(trpc.inventory.onHand.queryOptions());
  const negatives = useQuery(trpc.inventory.negatives.queryOptions());
  const onOrderQ = useQuery(trpc.purchasing.onOrder.queryOptions());
  const lowQ = useQuery(trpc.inventory.lowStock.queryOptions());
  const [tab, setTab] = useState('stock');
  const review = usePricingReviewWrite();
  const [tabBlocked, setTabBlocked] = useState(false);

  const setUp = useMutation(
    trpc.inventory.setUpLocations.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );

  const locations = (onHand.data?.locations ?? []) as StockLocation[];
  const items = (onHand.data?.items ?? []) as OnHandItem[];
  const onOrder = new Map(
    (onOrderQ.data ?? []).map((o) => [o.itemId, o.onOrderHundredths]),
  );

  const header = (
    <PageHeader
      title="Inventory"
      sub="What you own, what's coming, and who you buy from."
    />
  );
  const tabs = (
    <UnderlineTabs
      value={tab}
      onValueChange={(next) => {
        if (review.locked) {
          setTabBlocked(true);
          return;
        }
        setTabBlocked(false);
        setTab(next);
      }}
    >
      <UnderlineTabStrip
        tabs={[
          ...TABS,
          ...(manager ? [{ key: 'vans', label: 'Van templates' }] : []),
          ...(pricingAllowed ? [{ key: 'prices', label: 'Prices' }] : []),
        ]}
      />
    </UnderlineTabs>
  );
  // Pricing is independent of stock setup and stock query availability. All hooks ran above.
  if (tab === 'prices')
    return (
      <PageRoot>
        {header}
        {tabs}
        {pricingAllowed ? (
          <>
            {tabBlocked && review.locked ? (
              <Notice tone="info">
                Resolve the retained price review before changing Inventory tabs.
              </Notice>
            ) : null}
            <Suspense fallback={<PageSkeleton />}>
              <PricesTab review={review} />
            </Suspense>
          </>
        ) : (
          <ForbiddenState icon={IconInventory} what="material prices" />
        )}
      </PageRoot>
    );

  // Contacts and their protected evidence are independent of stock setup/availability.
  if (tab === 'vendors')
    return (
      <PageRoot>
        {header}
        {tabs}
        <Suspense fallback={<PageSkeleton />}>
          <VendorsTab />
        </Suspense>
      </PageRoot>
    );

  const stockState = shownState(onHand, onHand.data);
  if (stockState === 'offline')
    return (
      <PageRoot>
        {header}
        {tabs}
        <OfflineState icon={IconInventory} what="your stock" />
      </PageRoot>
    );
  if (stockState === 'loading')
    return (
      <PageRoot>
        {header}
        {tabs}
        <PageSkeleton />
      </PageRoot>
    );
  if (stockState === 'error') {
    return (
      <PageRoot>
        {header}
        {tabs}
        <ErrorState
          icon={IconInventory}
          what="your stock"
          onRetry={() => void onHand.refetch()}
        />
      </PageRoot>
    );
  }

  // Nowhere to put anything yet — the one-press setup is the whole screen.
  if (locations.length === 0) {
    return (
      <PageRoot>
        {header}
        {tabs}
        {setUp.error ? <Notice tone="danger">{setUp.error.message}</Notice> : null}
        <EmptyState
          icon={IconInventory}
          title="No stock locations yet"
          description="Stock lives somewhere — a warehouse, and a van per tech. Set those up and you can start counting parts."
          action={
            <Button
              onClick={() => setUp.mutate({ ...idem() })}
              disabled={setUp.isPending}
            >
              {setUp.isPending ? 'Setting up…' : 'Set up my locations'}
            </Button>
          }
        />
      </PageRoot>
    );
  }

  // A successful persisted response from before048 has no summary until it refreshes.
  const valuation = onHand.data?.valuation;
  /**
   * ⚠️ The page gates on `onHand` only; `negatives` and `lowQ` are their own questions and
   * were gated nowhere. `?? 0` then rendered a confident zero on both tiles — "nothing is
   * below its reorder point" and "nothing needs reconciling" are the two claims on this
   * page a shop acts on by NOT acting.
   *
   * `—` rather than a skeleton, matching the Stock value tile directly above, whose comment
   * makes the same argument about a $0 that would be a lie about the shelf.
   */
  const negativeCount = negatives.data?.length;
  const lowCount = lowQ.data?.length;

  return (
    <PageRoot>
      {header}

      <StaleNotice
        show={refreshFailed(onHand, onHand.data)}
        onRetry={() => void onHand.refetch()}
      />

      {/* Same dead end, two more tiles: `lowCount`/`negativeCount` are `—` on a failure and
          nothing offered a way to ask again. Raised by the Codex adversary on 018. */}
      <FiguresNotice
        show={
          (lowQ.isError && lowQ.data === undefined) ||
          (negatives.isError && negatives.data === undefined)
        }
        what="what needs reordering or reconciling"
        onRetry={() => {
          void lowQ.refetch();
          void negatives.refetch();
        }}
      />
      <KpiGrid>
        <KpiCard label="Parts counted" value={items.length} />
        <KpiCard
          label="Stock value"
          // Honest: a shop that has counted parts but never received one at a price has no
          // value to report, and saying $0 would be a lie about the shelf.
          value={
            valuation?.totalCostCents == null ? '—' : money(valuation.totalCostCents)
          }
        />
        <KpiCard
          label="Below reorder point"
          value={lowCount ?? '—'}
          tone={lowCount !== undefined && lowCount > 0 ? 'warn' : undefined}
        />
        <KpiCard
          label="Needs reconciling"
          value={negativeCount ?? '—'}
          tone={negativeCount !== undefined && negativeCount > 0 ? 'danger' : undefined}
        />
      </KpiGrid>

      {valuation?.missingCostBasis ? (
        <Notice tone="warn">
          {valuation.missingCostBasis}{' '}
          {valuation.missingCostBasis === 1 ? 'part has' : 'parts have'} stock without a
          cost basis. Stock value includes only parts with a known basis.
        </Notice>
      ) : null}

      {negatives.data && negatives.data.length > 0 ? (
        <Notice tone="warn">
          {negativeCount === 1
            ? 'One part is showing below zero'
            : `${negativeCount} parts are showing below zero`}{' '}
          — someone used stock the books didn't have. That's allowed; count them to put it
          right.{' '}
          {negatives.data.slice(0, 3).map((n, i) => (
            <span key={`${n.itemId}-${n.locationId}`}>
              {i > 0 ? ', ' : ''}
              <strong>
                {n.itemName} ({formatQty(n.ownHundredths)} at {n.locationName})
              </strong>
            </span>
          ))}
          {negatives.data.length > 3 ? `, and ${negatives.data.length - 3} more` : ''}.
        </Notice>
      ) : null}

      {tabs}

      {tab === 'vans' && manager ? (
        <VanTemplatesTab onManageLocations={() => setTab('stock')} />
      ) : tab === 'stock' ? (
        <StockTab items={items} locations={locations} onOrder={onOrder} />
      ) : tab === 'reorder' ? (
        <ReorderTab onOpenTab={setTab} />
      ) : tab === 'orders' ? (
        <PurchaseOrdersTab items={items} locations={locations} />
      ) : null}
    </PageRoot>
  );
}
